import { useState } from "react";
import { useLang } from "@/pages/home/context/LangContext";

const features = [
  {
    icon: "ri-user-heart-line",
    color: "text-gold",
    bg: "bg-gold/10",
    en: {
      title: "Guest Management",
      desc: "Full guest lifecycle — check-in, room changes, check-out, and history. Bilingual profiles with passport, ID and residence-card details.",
      bullets: ["Multi-guest check-in", "Guest timeline & history", "Nationality breakdown analytics"],
    },
    ar: {
      title: "إدارة النزلاء",
      desc: "دورة حياة الضيف الكاملة — تسجيل الوصول، تغيير الغرف، المغادرة، والسجل. ملفات شخصية ثنائية اللغة مع بيانات الجواز والهوية والإقامة.",
      bullets: ["تسجيل وصول متعدد الضيوف", "الجدول الزمني وسجل الضيف", "تحليلات توزيع الجنسيات"],
    },
  },
  {
    icon: "ri-shield-check-line",
    color: "text-green-600",
    bg: "bg-green-50",
    en: {
      title: "Automated Rasad Compliance",
      desc: "Automatic submission to the national Rasad platform. Zero manual effort — every guest event is logged and reported in real time.",
      bullets: ["Auto-submit on check-in/out", "Compliance score dashboard", "Failed submission retry queue"],
    },
    ar: {
      title: "الامتثال التلقائي لرصد",
      desc: "إرسال تلقائي إلى منصة رصد الوطنية. صفر جهد يدوي — كل حدث ضيف يُسجَّل ويُبلَّغ عنه في الوقت الفعلي.",
      bullets: ["إرسال تلقائي عند الوصول/المغادرة", "لوحة درجة الامتثال", "قائمة إعادة محاولة الإرسال الفاشل"],
    },
  },
  {
    icon: "ri-car-line",
    color: "text-teal-600",
    bg: "bg-teal-50",
    en: {
      title: "Car Rental Management",
      desc: "Full vehicle fleet management — availability calendar, rental booking, driver licence details, and return tracking.",
      bullets: ["Fleet availability calendar", "Driver licence records", "Automated return reminders"],
    },
    ar: {
      title: "إدارة تأجير السيارات",
      desc: "إدارة أسطول المركبات الكاملة — تقويم التوفر، حجز التأجير، بيانات رخصة القيادة، وتتبع الإعادة.",
      bullets: ["تقويم توفر الأسطول", "سجل رخص القيادة", "تذكيرات الإعادة التلقائية"],
    },
  },
  {
    icon: "ri-bar-chart-box-line",
    color: "text-gold",
    bg: "bg-gold/10",
    en: {
      title: "Real-time Reporting",
      desc: "Live dashboards with occupancy rates, revenue trends, and compliance scores. Export to PDF, Excel, or schedule automated reports.",
      bullets: ["Occupancy & revenue charts", "Nationality mix analytics", "PDF/Excel export & scheduling"],
    },
    ar: {
      title: "تقارير فورية",
      desc: "لوحات تحكم مباشرة بمعدلات الإشغال واتجاهات الإيرادات ودرجات الامتثال. تصدير إلى PDF أو Excel أو جدولة تقارير تلقائية.",
      bullets: ["مخططات الإشغال والإيرادات", "تحليلات مزيج الجنسيات", "تصدير PDF/Excel والجدولة"],
    },
  },
  {
    icon: "ri-building-4-line",
    color: "text-orange-500",
    bg: "bg-orange-50",
    en: {
      title: "Multi-property Support",
      desc: "Manage multiple hotels or rental branches from a single account. Unified reporting and compliance across all locations.",
      bullets: ["Property switcher dashboard", "Unified cross-property reports", "Per-property compliance scores"],
    },
    ar: {
      title: "دعم متعدد العقارات",
      desc: "إدارة فنادق متعددة أو فروع تأجير من حساب واحد. تقارير وامتثال موحد عبر جميع المواقع.",
      bullets: ["لوحة تبديل العقارات", "تقارير موحدة عبر العقارات", "درجات امتثال لكل عقار"],
    },
  },
  {
    icon: "ri-qr-scan-line",
    color: "text-[#92400E]",
    bg: "bg-[#FEF3C7]",
    soon: true,
    en: {
      title: "Document Scanner",
      desc: "Passport and ID scanning with MRZ reading is on our roadmap. Today, a guided form captures every document field Rasad needs.",
      bullets: ["Guided passport & ID entry", "MRZ scanning", "USB scanner support"],
    },
    ar: {
      title: "ماسح الوثائق",
      desc: "مسح الجواز والهوية مع قراءة MRZ ضمن خطتنا القادمة. حالياً، يجمع نموذج موجَّه كل بيانات الوثيقة التي يحتاجها رصد.",
      bullets: ["إدخال موجَّه للجواز والهوية", "قراءة MRZ", "دعم ماسحات USB"],
    },
  },
];

// Product facts only — no customer-count or rating claims until we
// have numbers we can stand behind.
const stats = [
  { value: "14", en: "Day free trial", ar: "يوماً تجربة مجانية" },
  { value: "EN · ع", en: "Fully bilingual", ar: "ثنائي اللغة بالكامل" },
  { value: "2-in-1", en: "Hotels & car rentals", ar: "فنادق وتأجير سيارات" },
  { value: "Rasad", en: "Automatic submission", ar: "إرسال تلقائي" },
];

const workflow = [
  { step: 1, icon: "ri-passport-line", en: "Enter Documents", ar: "إدخال الوثائق", descEn: "Guided passport or ID entry — every field Rasad needs", descAr: "إدخال موجَّه للجواز أو الهوية — كل ما يحتاجه رصد" },
  { step: 2, icon: "ri-hotel-bed-line", en: "Assign Room", ar: "تعيين الغرفة", descEn: "Pick from available rooms with capacity check", descAr: "اختر من الغرف المتاحة مع فحص السعة" },
  { step: 3, icon: "ri-shield-check-line", en: "Auto-Submit Rasad", ar: "إرسال رصد تلقائي", descEn: "Compliance submitted automatically in seconds", descAr: "يُرسَل الامتثال تلقائياً في ثوانٍ" },
  { step: 4, icon: "ri-check-double-line", en: "Done!", ar: "تم!", descEn: "Guest checked in, receipt printed, all logged", descAr: "الضيف مسجّل، الإيصال مطبوع، كل شيء مسجّل" },
];

export default function Features() {
  const { isAr, t } = useLang();
  const [activeFeature, setActiveFeature] = useState(0);

  return (
    <section id="features" dir={isAr ? "rtl" : "ltr"} className="py-24 bg-cream">
      <div className="max-w-7xl mx-auto px-4 md:px-8">

        {/* Header */}
        <div className="mb-16 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold/10 border border-gold/20 mb-4">
            <i className="ri-star-line text-gold text-sm"></i>
            <span className={`text-[13px] font-semibold text-[#B8892A] ${isAr ? "font-tajawal" : ""}`}>
              {t("Core Features", "المميزات الأساسية")}
            </span>
          </div>
          <h2 className={`font-bold text-3xl md:text-4xl text-ink mb-4 ${isAr ? "font-tajawal" : "font-inter"}`}>
            {t("Everything You Need to Run Your Hospitality", "كل ما تحتاجه لإدارة ضيافتك")}
          </h2>
          <p className={`text-base md:text-lg max-w-2xl mx-auto text-muted ${isAr ? "font-tajawal" : ""}`}>
            {t(
              "From guest registration to automated compliance — Diyafa covers every aspect of your hospitality business.",
              "من تسجيل الضيوف إلى الامتثال التلقائي — تغطي ضيافة كل جانب من جوانب عملك في مجال الضيافة."
            )}
          </p>
        </div>

        {/* Feature Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-20">
          {features.map((f, i) => (
            <div
              key={i}
              onClick={() => setActiveFeature(i)}
              className={`group p-6 rounded-2xl border transition-all duration-300 cursor-pointer ${
                activeFeature === i
                  ? "bg-ink border-gold/40"
                  : "bg-white border-line hover:-translate-y-1"
              }`}
            >
              <div className={`flex items-start gap-4 mb-4 ${isAr ? "flex-row-reverse" : ""}`}>
                <div className={`w-12 h-12 flex items-center justify-center rounded-2xl flex-shrink-0 transition-all ${
                  activeFeature === i ? "bg-gold/20" : f.bg
                }`}>
                  <i className={`${f.icon} text-xl ${activeFeature === i ? "text-gold" : f.color}`}></i>
                </div>
                <div className={isAr ? "text-right" : "text-left"}>
                  <h3 className={`font-bold text-base mb-1 ${isAr ? "font-tajawal" : "font-inter"} ${activeFeature === i ? "text-white" : "text-ink"}`}>
                    {isAr ? f.ar.title : f.en.title}
                    {f.soon && (
                      <span className="ms-2 align-middle inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gold-soft text-gold-ink border border-line-strong">
                        {isAr ? "قريباً" : "Coming soon"}
                      </span>
                    )}
                  </h3>
                  <p className={`text-sm leading-relaxed ${isAr ? "font-tajawal" : ""} ${activeFeature === i ? "text-white/60" : "text-muted"}`}>
                    {isAr ? f.ar.desc : f.en.desc}
                  </p>
                </div>
              </div>
              <ul className={`space-y-1.5 ${isAr ? "text-right" : ""}`}>
                {(isAr ? f.ar.bullets : f.en.bullets).map((b, j) => (
                  <li key={j} className={`flex items-center gap-2 text-xs ${isAr ? "flex-row-reverse" : ""}`}>
                    <i className={`ri-check-line flex-shrink-0 ${activeFeature === i ? "text-gold" : "text-gold"}`}></i>
                    <span className={`${isAr ? "font-tajawal" : ""} ${activeFeature === i ? "text-white/70" : "text-ink-soft"}`}>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Workflow Section */}
        <div className="mb-20">
          <div className="text-center mb-10">
            <h3 className={`font-bold text-2xl md:text-3xl text-ink mb-3 ${isAr ? "font-tajawal" : "font-inter"}`}>
              {t("Check-In in Under 2 Minutes", "تسجيل الوصول في أقل من دقيقتين")}
            </h3>
            <p className={`text-base text-muted max-w-xl mx-auto ${isAr ? "font-tajawal" : ""}`}>
              {t("Our streamlined workflow gets guests checked in fast — with full Rasad compliance handled automatically.", "سير العمل المبسّط لدينا يُسجّل الضيوف بسرعة — مع التعامل التلقائي مع امتثال رصد.")}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {workflow.map((w, i) => (
              <div key={i} className="relative">
                <div className="bg-white rounded-2xl p-6 border border-line text-center">
                  <div className="w-12 h-12 rounded-2xl bg-gold/10 flex items-center justify-center mx-auto mb-3">
                    <i className={`${w.icon} text-gold text-xl`}></i>
                  </div>
                  <div className="w-6 h-6 rounded-full bg-ink flex items-center justify-center mx-auto mb-3">
                    <span className="text-white text-xs font-bold">{w.step}</span>
                  </div>
                  <h4 className={`font-bold text-sm text-ink mb-1 ${isAr ? "font-tajawal" : ""}`}>
                    {isAr ? w.ar : w.en}
                  </h4>
                  <p className={`text-xs text-muted ${isAr ? "font-tajawal" : ""}`}>
                    {isAr ? w.descAr : w.descEn}
                  </p>
                </div>
                {i < workflow.length - 1 && (
                  <div className={`hidden lg:block absolute top-1/2 -translate-y-1/2 text-gold/40 text-xl ${isAr ? "-left-3" : "-right-3"} z-10`}>
                    <i className={isAr ? "ri-arrow-left-line" : "ri-arrow-right-line"}></i>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 p-8 rounded-3xl bg-ink">
          {stats.map((s, i) => (
            <div key={i} className="text-center">
              <p className="font-inter font-bold text-3xl text-gold mb-1">{s.value}</p>
              <p className={`text-sm text-white/60 ${isAr ? "font-tajawal" : ""}`}>{isAr ? s.ar : s.en}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
